import { Fragment, useEffect, useMemo, useReducer, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Box, Col, Contain, Row } from '../components/Grid';
import ErrorMessage from '../components/Search/ErrorMessage';
import {
  initialState,
  LOAD_MORE_LIMIT,
  ResultSet,
  SearchQuery,
  searchReducer,
} from '../components/Search/searchElements';
import SeoHead from '../components/SeoHead';
import Spinner from '../components/Spinner';
import Tile from '../components/Tile';
import { getSearchResults } from '../services/api';

const Search = () => {
  const Router = useRouter();
  const { q, type } = Router.query;

  const [state, dispatch] = useReducer(searchReducer, initialState);
  const loadingRef = useRef();
  const yRef = useRef(0);

  const loadMore = () => {
    dispatch({
      type: 'LOADING',
    });
    const nextPage = state.detail.page + 1;

    let totalCount = 0;
    if (state.results[0]?.data?.list?.length) {
      totalCount = state.detail.page * LOAD_MORE_LIMIT;
    }
    totalCount = state.results[0]?.data?.list?.length;

    dispatch({
      type: 'MORE_RESULTS',
      payload: {
        type,
        page: nextPage,
        limit: totalCount,
      },
    });
  };

  useEffect(() => {
    if (q) {
      const searchFetcher = async (query) => {
        try {
          const response = await getSearchResults(query);
          dispatch({
            type: 'SET_RESULTS',
            payload: {
              results: response?.sections || [],
              type,
            },
          });
        } catch (e) {
          dispatch({
            type: 'SET_ERROR',
            value: e.message,
          });
        }
      };
      searchFetcher(q);
    }
  }, [q, type]);

  useEffect(() => {
    let cleanupFunction; // Declare a variable for the cleanup function

    if (loadingRef.current) {
      const observer = new IntersectionObserver(
        (entities) => {
          const { y } = entities[0].boundingClientRect;
          if (yRef.current > y) {
            loadMore();
          }
          yRef.current = y;
        },
        {
          root: null,
          threshold: 1.0,
        },
      );

      observer.observe(loadingRef.current);

      cleanupFunction = () => {
        observer.disconnect();
      };
    }

    return cleanupFunction; // Consistent return with value (may be undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.detail.page]);

  const loadingTrigger = useMemo(
    () => (state.status === 'loading' ? <Spinner /> : 'load more'),
    [state.status],
  );

  return (
    <Fragment>
      <SeoHead
        title="Search results | Link Rewards Programme"
        description="Looking for a specific term but not sure where to find it? Quickly search through our entire Link website to find what you're looking for."
      />
      <SearchQuery>
        <Contain>
          <div>Search results for: &quot;{q}&quot;</div>
        </Contain>
      </SearchQuery>
      <Contain data-testid="scroll-container">
        {state.status === 'loading' && <Spinner />}
        {state.status === 'complete' && (!state.results || state.results.length === 0) ? (
          <ErrorMessage {...(state.error && { message: state.error })} />
        ) : (
          state.results.map(({ title, data, viewType }) => {
            const urlPrefix = viewType === 'partners' ? 'partner' : 'promotion';
            const total = data.list.length;

            return (
              <ResultSet key={viewType}>
                <div className="heading">
                  <strong>{title}</strong>
                  {total > state.limit && !state.detail.type && (
                    <Link
                      href={{
                        pathname: Router.pathname,
                        query: { ...Router.query, type: viewType },
                      }}
                      className="view-all hidden-mobile"
                    >
                      View all {total} {title}
                    </Link>
                  )}
                </div>
                <Row
                  as="ul"
                  className={`list ${viewType === 'partners' ? 'merchant-list' : ''}`}
                  flexDirection={['column', 'row']}
                  flexWrap={['nowrap', 'wrap']}
                >
                  {data.list.slice(0, state.limit).map((item) => (
                    <Col as="li" key={item.id} width="100%" maxWidth={['none', '33.33%']} mb="md">
                      <Link
                        href={`discover/${urlPrefix}/${item.slug}`}
                        as={`discover/${urlPrefix}/${item.slug}`}
                        className="item"
                      >
                        <Tile
                          banner={viewType !== 'partners' && item.imgUrls?.landscapeBanner}
                          logo={viewType === 'partners' && item.imgUrls?.logo}
                          title={item.name}
                          partner={viewType === 'partners'}
                        />
                      </Link>
                    </Col>
                  ))}
                </Row>
                {total > state.limit && !state.detail.type && (
                  <Box className="text-center" mt="-0.625rem" mb="xl">
                    <Link
                      href={{
                        pathname: Router.pathname,
                        query: { ...Router.query, type: viewType },
                      }}
                      className="view-all visible-mobile"
                    >
                      View all {total} {title}
                    </Link>
                  </Box>
                )}
                {state.detail.type && state.limit < total && (
                  <Box ref={loadingRef} my="lg" className="text-center">
                    {loadingTrigger}
                  </Box>
                )}
              </ResultSet>
            );
          })
        )}
      </Contain>
    </Fragment>
  );
};

export default Search;
